import type { BackupObjectMetadata } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { FormPanel } from '../../components/FormPanel';
import { Input } from '../../components/Input';
import { KpiCard } from '../../components/KpiCard';
import { Pill } from '../../components/Pill';
import { Select } from '../../components/Select';
import { listBranches } from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import {
  branchDisplayLabel,
  deviceDisplayLabel,
  missingDeviceLabels,
} from '../../lib/displayIdentity';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';
import { OperationsHeader } from './OperationsHeader';

function fmtDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('th-TH', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function fmtBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function fmtTotalBytes(value: number): string {
  if (value < 1024) return `${value} ไบต์`;
  return fmtBytes(value);
}

function fmtAge(createdAt: string): string {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return '—';
  const hours = Math.max(0, (Date.now() - created) / 3_600_000);
  if (hours < 1) return '< 1 ชม.';
  if (hours < 48) return `${Math.floor(hours)} ชม.`;
  return `${Math.floor(hours / 24)} วัน`;
}

function shortChecksum(value: string): string {
  return `${value.slice(0, 12)}…${value.slice(-8)}`;
}

function filenameFor(objectKey: string): string {
  return objectKey.split('/').at(-1) ?? 'backup.sqlite.gz.enc';
}

function triggerDownload(blob: Blob, objectKey: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filenameFor(objectKey);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function filterBackupsByDate(
  backups: BackupObjectMetadata[],
  from: string,
  to: string,
): BackupObjectMetadata[] {
  if (!from && !to) return backups;
  const fromMs = from ? new Date(`${from}T00:00:00.000`).getTime() : null;
  const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
  return backups.filter((backup) => {
    const created = new Date(backup.createdAt).getTime();
    if (Number.isNaN(created)) return false;
    if (fromMs !== null && created < fromMs) return false;
    if (toMs !== null && created > toMs) return false;
    return true;
  });
}

export function BackupListView() {
  const user = useSession((state) => state.user);
  const selectedShellBranchId = useUi((state) => state.branchId);
  const [branchId, setBranchId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [seenDeviceIds, setSeenDeviceIds] = useState<Record<string, string[]>>(
    {},
  );
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadedObjectKey, setDownloadedObjectKey] = useState<string | null>(
    null,
  );
  const [downloadingObjectKey, setDownloadingObjectKey] = useState<
    string | null
  >(null);

  const canAccess = user?.role === 'owner' || user?.role === 'manager';
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
    enabled: canAccess,
  });
  const branches = branchesQuery.data ?? [];
  const allowedBranchIds = useMemo(
    () =>
      user
        ? visibleBranchIds(
            user,
            branches.map((b) => b.id),
          )
        : [],
    [branches, user],
  );

  useEffect(() => {
    if (!canAccess || allowedBranchIds.length === 0) return;
    setBranchId((current) => {
      if (current && allowedBranchIds.includes(current)) return current;
      if (
        selectedShellBranchId &&
        allowedBranchIds.includes(selectedShellBranchId)
      ) {
        return selectedShellBranchId;
      }
      return allowedBranchIds[0] ?? '';
    });
  }, [allowedBranchIds, canAccess, selectedShellBranchId]);

  const devicesQuery = useQuery({
    queryKey: ['device-health', branchId],
    queryFn: () => dashboardApi.getDeviceHealth({ branchId }),
    enabled: canAccess && !!branchId,
  });

  const backupsQuery = useQuery({
    queryKey: ['backups', branchId, deviceId, from, to],
    queryFn: () =>
      dashboardApi.listBackups({
        branchId,
        deviceId: deviceId.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
      }),
    enabled: canAccess && !!branchId,
  });

  // Preserve historical machines discovered in this branch when filters narrow
  // the response. This directory lives only for the lifetime of this screen.
  useEffect(() => {
    const ids = (backupsQuery.data?.backups ?? [])
      .filter((backup) => backup.branchId === branchId)
      .map((backup) => backup.deviceId);
    if (!branchId || !ids.length) return;
    setSeenDeviceIds((current) => {
      const previous = current[branchId] ?? [];
      const merged = [...new Set([...previous, ...ids])];
      return merged.length === previous.length
        ? current
        : { ...current, [branchId]: merged };
    });
  }, [branchId, backupsQuery.data]);

  const backups = useMemo(
    () => filterBackupsByDate(backupsQuery.data?.backups ?? [], from, to),
    [backupsQuery.data, from, to],
  );
  const devices = useMemo(() => {
    const known = new Map(
      (devicesQuery.data?.devices ?? [])
        .filter((device) => device.branchId === branchId)
        .map((device) => [device.deviceId, deviceDisplayLabel(device)]),
    );
    const missing = missingDeviceLabels(
      // Keep the active filter representable during loading and empty results.
      [
        ...(seenDeviceIds[branchId] ?? []),
        ...backups.map((backup) => backup.deviceId),
        ...(deviceId ? [deviceId] : []),
      ].filter((id) => !known.has(id)),
      devicesQuery.isSuccess
        ? 'เครื่องที่ไม่พบข้อมูล'
        : devicesQuery.isError
          ? 'โหลดชื่อเครื่องไม่สำเร็จ'
          : 'กำลังโหลดเครื่อง',
    );
    for (const [id, label] of missing) known.set(id, label);
    return known;
  }, [
    devicesQuery.data,
    devicesQuery.isSuccess,
    devicesQuery.isError,
    backups,
    branchId,
    deviceId,
    seenDeviceIds,
  ]);
  const backupSummary = useMemo(() => {
    const deviceCount = new Set(backups.map((backup) => backup.deviceId)).size;
    const totalBytes = backups.reduce(
      (total, backup) => total + backup.encryptedSizeBytes,
      0,
    );
    const latest = backups.reduce<BackupObjectMetadata | null>(
      (current, backup) =>
        !current || backup.createdAt > current.createdAt ? backup : current,
      null,
    );
    return { deviceCount, totalBytes, latest };
  }, [backups]);

  const download = async (backup: BackupObjectMetadata): Promise<void> => {
    setDownloadError(null);
    setDownloadedObjectKey(null);
    setDownloadingObjectKey(backup.objectKey);
    try {
      const blob = await dashboardApi.downloadBackup(backup.objectKey);
      triggerDownload(blob, backup.objectKey);
      setDownloadedObjectKey(backup.objectKey);
    } catch (error) {
      setDownloadError(
        error instanceof Error ? error.message : 'ดาวน์โหลดไฟล์สำรองไม่สำเร็จ',
      );
    } finally {
      setDownloadingObjectKey(null);
    }
  };

  if (!canAccess) {
    return (
      <div className="bo-fade">
        <OperationsHeader
          title="สำรองข้อมูล"
          subtitle="ดูไฟล์สำรองของแต่ละสาขาและดาวน์โหลดเมื่อจำเป็นต้องกู้คืนข้อมูล"
          status={{ label: 'จำกัดสิทธิ์ผู้ดูแล', tone: 'neutral' }}
        />
        <div className="bo-card">
          <EmptyState
            title="ไม่มีสิทธิ์เข้าถึงไฟล์สำรอง"
            sub="เฉพาะผู้จัดการหรือเจ้าของเท่านั้น"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <OperationsHeader
        title="สำรองข้อมูล"
        subtitle="ติดตามไฟล์สำรองที่ส่งจากเครื่อง POS ตรวจเวลาล่าสุด และดาวน์โหลดไฟล์สำหรับการกู้คืน"
        status={
          backupsQuery.isLoading
            ? { label: 'กำลังโหลดข้อมูลสำรอง', tone: 'neutral' }
            : backups.length > 0
              ? { label: 'พบข้อมูลสำรอง', tone: 'ok' }
              : { label: 'ยังไม่พบข้อมูลสำรอง', tone: 'warn' }
        }
      />

      <FormPanel className="mb-6 grid gap-4 md:grid-cols-4">
        <Field label="สาขา">
          <Select
            aria-label="สาขา"
            value={branchId}
            onChange={(event) => {
              setDeviceId('');
              setBranchId(event.target.value);
            }}
          >
            {branches
              .filter((branch) => allowedBranchIds.includes(branch.id))
              .map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branchDisplayLabel(branch)}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="เครื่อง">
          <Select
            aria-label="เครื่อง"
            value={deviceId}
            onChange={(event) => setDeviceId(event.target.value)}
          >
            <option value="">ทุกเครื่อง</option>
            {[...devices].map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </Select>
          {devicesQuery.isLoading && (
            <p role="status" className="text-sm-label text-ink-3">
              กำลังโหลดเครื่อง
            </p>
          )}
          {devicesQuery.isError && (
            <div role="alert" className="text-sm-label">
              โหลดรายชื่อเครื่องไม่สำเร็จ
              <button
                type="button"
                className="ml-2 underline"
                onClick={() => void devicesQuery.refetch()}
              >
                ลองโหลดรายชื่อเครื่องอีกครั้ง
              </button>
            </div>
          )}
        </Field>
        <Field label="จากวันที่">
          <Input
            aria-label="จากวันที่"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </Field>
        <Field label="ถึงวันที่">
          <Input
            aria-label="ถึงวันที่"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </Field>
      </FormPanel>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="ไฟล์ในช่วงที่เลือก"
          value={backupsQuery.isLoading ? '…' : backups.length}
          unit="ไฟล์"
          sub="ตามตัวกรองด้านบน"
        />
        <KpiCard
          label="เครื่องที่ส่งข้อมูล"
          value={backupsQuery.isLoading ? '…' : backupSummary.deviceCount}
          unit="เครื่อง"
          sub="ในผลลัพธ์ชุดนี้"
          tone="accent"
        />
        <KpiCard
          label="พื้นที่รวม"
          value={
            backupsQuery.isLoading
              ? '…'
              : fmtTotalBytes(backupSummary.totalBytes)
          }
          sub="ขนาดหลังเข้ารหัส"
          tone="ok"
        />
        <KpiCard
          label="สำรองล่าสุด"
          value={
            backupsQuery.isLoading
              ? '…'
              : backupSummary.latest
                ? fmtAge(backupSummary.latest.createdAt)
                : '—'
          }
          sub={
            backupSummary.latest
              ? fmtDateTime(backupSummary.latest.createdAt)
              : 'ยังไม่มีไฟล์ในช่วงนี้'
          }
          tone="warn"
        />
      </div>

      {backupsQuery.isLoading || branchesQuery.isLoading ? (
        <div className="bo-card text-ink-3">กำลังโหลดรายการไฟล์สำรอง…</div>
      ) : branchesQuery.isError ? (
        <div className="bo-card">
          <EmptyState
            title="โหลดข้อมูลสาขาไม่สำเร็จ"
            sub={
              branchesQuery.error instanceof Error
                ? branchesQuery.error.message
                : 'ตรวจสอบสิทธิ์และข้อมูลสาขา แล้วลองใหม่'
            }
          />
        </div>
      ) : backupsQuery.isError ? (
        <div className="bo-card">
          <EmptyState
            title="โหลดรายการไฟล์สำรองไม่สำเร็จ"
            sub={
              backupsQuery.error instanceof Error
                ? backupsQuery.error.message
                : 'ลองใหม่อีกครั้ง หากยังไม่ได้ให้แจ้งผู้ดูแลระบบ'
            }
          />
        </div>
      ) : backups.length === 0 ? (
        <div className="bo-card">
          <EmptyState
            title="ยังไม่มีไฟล์สำรองในช่วงนี้"
            sub="ลองเปลี่ยนสาขา เครื่อง หรือช่วงวันที่"
          />
        </div>
      ) : (
        <div className="bo-card overflow-x-auto">
          <table className="bo-table">
            <thead>
              <tr>
                <th>เวลา</th>
                <th>สาขา / เครื่อง</th>
                <th>ขนาด</th>
                <th>ค่าตรวจสอบไฟล์</th>
                <th>อายุ</th>
                <th>สถานะ</th>
                <th>ไฟล์</th>
                <th>ดาวน์โหลด</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((backup) => (
                <tr key={backup.objectKey}>
                  <td>{fmtDateTime(backup.createdAt)}</td>
                  <td>
                    <div className="font-semibold">
                      {branchDisplayLabel(
                        branches.find(
                          (branch) => branch.id === backup.branchId,
                        ),
                      )}
                    </div>
                    <div className="text-sm-label text-ink-3">
                      {devices.get(backup.deviceId) || 'ไม่พบข้อมูลเครื่อง'}
                    </div>
                  </td>
                  <td className="num">{fmtBytes(backup.encryptedSizeBytes)}</td>
                  <td className="font-mono text-sm-label">
                    {shortChecksum(backup.sha256)}
                  </td>
                  <td>{fmtAge(backup.createdAt)}</td>
                  <td>
                    <Pill tone="ok">{backup.retentionClass}</Pill>
                  </td>
                  <td className="font-mono text-sm-label">
                    {backup.objectKey.split('/').at(-1)}
                    <details>
                      <summary className="cursor-pointer">
                        ข้อมูลทางเทคนิค
                      </summary>
                      <code className="break-all">{backup.objectKey}</code>
                    </details>
                  </td>
                  <td>
                    <Btn
                      size="sm"
                      onClick={() => void download(backup)}
                      disabled={downloadingObjectKey === backup.objectKey}
                    >
                      {downloadingObjectKey === backup.objectKey
                        ? 'กำลังดาวน์โหลด'
                        : 'ดาวน์โหลด'}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {downloadError ? (
        <div className="mt-4 rounded-card border border-danger/30 bg-danger/5 px-4 py-3 text-danger">
          {downloadError}
        </div>
      ) : null}
      {downloadedObjectKey ? (
        <div className="mt-4 rounded-card border border-ok/30 bg-ok/5 px-4 py-3 text-ok">
          ดาวน์โหลดแล้ว
        </div>
      ) : null}
    </div>
  );
}
